/**
 * MemoryDashboardSettings — Admin settings for the Memory Dashboard
 *
 * Allows admins to:
 * - Enable/disable the Memory Dashboard for themselves
 * - View quick links to the dashboard
 * - See current memory system status
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Brain,
  ToggleLeft,
  ToggleRight,
  ExternalLink,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Info
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import apiClient from '../../lib/apiClient';
import { toast } from '../Toast';
import './MemoryDashboardSettings.css';

function MemoryDashboardSettings() {
  const navigate = useNavigate();
  const { preferences, updatePreference, loading: prefsLoading } = useUserPreferences();

  // Get current enabled state (default to true if not set)
  const isEnabled = preferences?.memoryDashboardEnabled !== false;

  // Fetch quick health status
  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ['memory', 'health', 'quick'],
    queryFn: () => apiClient.get('/api/memory/health'),
    refetchInterval: 60000,
    retry: 1,
  });

  // updatePreference is optimistic internally (state flips immediately,
  // backend save is queued) — no spinner-swap needed (UI-responsiveness
  // review 2026-08-08).
  const handleToggle = () => {
    if (prefsLoading) return;
    updatePreference('memoryDashboardEnabled', !isEnabled)
      .catch((error) => {
        // Optimistic flip already happened — say so instead of leaving the
        // toggle silently lying (slice 4).
        console.error('Failed to toggle Memory Dashboard:', error);
        toast('Couldn’t save the Memory Dashboard setting — it may revert', 'error');
      });
  };

  const handleOpenDashboard = () => {
    navigate('/memory');
  };

  const getStatusDisplay = () => {
    if (healthLoading) {
      return (
        <div className="mds-status mds-status--loading">
          <Loader2 size={16} className="mds-spin" />
          <span>Checking status...</span>
        </div>
      );
    }

    if (!healthData) {
      return (
        <div className="mds-status mds-status--unknown">
          <AlertTriangle size={16} />
          <span>Unable to fetch status</span>
        </div>
      );
    }

    const status = healthData.status || 'unknown';
    const statusClass = status === 'pass' ? 'pass' : status === 'warn' ? 'warn' : 'fail';

    return (
      <div className={`mds-status mds-status--${statusClass}`}>
        {status === 'pass' ? (
          <CheckCircle2 size={16} />
        ) : (
          <AlertTriangle size={16} />
        )}
        <span>System {status === 'pass' ? 'Healthy' : status === 'warn' ? 'Warning' : 'Issues Detected'}</span>
      </div>
    );
  };

  return (
    <div className="mds-container">
      <header className="mds-header">
        <div className="mds-header-icon">
          <Brain size={24} />
        </div>
        <div className="mds-header-text">
          <h2 className="mds-title">Memory Dashboard</h2>
          <p className="mds-subtitle">
            Observability dashboard for the Sales AI memory system
          </p>
        </div>
      </header>

      {/* Quick Status */}
      <section className="mds-section">
        <h3 className="mds-section-title">System Status</h3>
        <div className="mds-status-card">
          {getStatusDisplay()}
          {isEnabled && (
            <button className="mds-link-btn" onClick={handleOpenDashboard}>
              <ExternalLink size={14} />
              Open Dashboard
            </button>
          )}
        </div>
      </section>

      {/* Toggle Setting */}
      <section className="mds-section">
        <h3 className="mds-section-title">Visibility</h3>
        <div className="mds-toggle-card">
          <div className="mds-toggle-info">
            <span className="mds-toggle-label">Enable Memory Dashboard</span>
            <span className="mds-toggle-description">
              Show the Memory Dashboard in your navigation. This is an admin-only feature.
            </span>
          </div>
          <button
            className={`mds-toggle-btn ${isEnabled ? 'mds-toggle-btn--on' : 'mds-toggle-btn--off'}`}
            onClick={handleToggle}
            disabled={prefsLoading}
            aria-label={isEnabled ? 'Disable Memory Dashboard' : 'Enable Memory Dashboard'}
          >
            {isEnabled ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
          </button>
        </div>
      </section>

      {/* Info Section */}
      <section className="mds-section">
        <h3 className="mds-section-title">About</h3>
        <div className="mds-info-card">
          <Info size={16} className="mds-info-icon" />
          <div className="mds-info-content">
            <p>
              The Memory Dashboard provides visibility into the Sales AI memory system,
              including:
            </p>
            <ul className="mds-feature-list">
              <li>System health monitoring</li>
              <li>Extraction and search statistics</li>
              <li>Dreaming scheduler status</li>
              <li>Items needing human review</li>
              <li>Memory distribution and feedback metrics</li>
            </ul>
            <p className="mds-note">
              This feature is only available to administrators.
              The dashboard can be accessed at <code>/memory</code> when enabled.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      {isEnabled && (
        <section className="mds-section">
          <h3 className="mds-section-title">Quick Actions</h3>
          <div className="mds-actions">
            <button className="mds-action-btn" onClick={handleOpenDashboard}>
              <Activity size={16} />
              View Dashboard
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export default MemoryDashboardSettings;
